import { Product } from "./Product.js";
import { Rect } from "./Rect.js";
import { ArrayUtil } from "./utility.js";

const settings = {
    "case": {
        "bleed": 50, // tbd
        // safeAreaInsets: amount for each product to inset around undrawable areas of the product such as the frame, camera, etc.
        // sticker edges would draw up to this location + any margin from borderStickerMargins.
        "safeAreaInsets": [0.02 /* top */, 0.04 /* right */, 0.02 /* bottom */, 0.04 /* left */],
        "cameraCutoutFrame": [0, 0, 0.59, 0.302], // x, y, w, h
        // userImageMargins: ratio of margins for each edge to apply to user image on top of safeAreaInsets. Currently only used for removeBackground + center/bottom.
        "userImageMargins": [0.035, 0.035, 0.035, 0.035],
        "snapshotExtraOffsetY": 60, // amt to y translate snapshot image past center
        "snapshotBorderWidth": 20,
        "snapshotInsetRatio": 0.08,
        "snapshotRotationRandomnessDeg": 5,
        // Stickers per row and column. Can be overridden by Device.patternCnt.
        "patternCnt": [5, 4],
        "patternRotationRandomnessDeg": 20,
        "patternTranslationRandomness": 0.008,
        // Vertical and horizontal sticker spacing [vertical, horizontal]. Can be overridden by Device.patternSpacing.
        "patternSpacing": [0.39, 0.25],
        // patternInsets: distance to inset 1st column and 1st row [column, row]. Can be overridden by Device.patternInsets.
        "patternInsets": [-0.03, -0.03],
        // patternStickerScale: downscaling for stickers in pattern mode. can be overridden with a patternStickerScale value on the sticker set itself.
        "patternStickerScale": 0.09,
        "borderRotationRandomnessDeg": 180,
        // borderStickerScale: downscaling for stickers in border mode. can be overridden with a borderStickerScale value on the sticker set itself.
        "borderStickerScale": 0.1,
        // cornerLargeStickerInsetRatio: ratio of width (not height; so its even on all sides) to inset (move toward center) stickers when placed in corner as "large" (with photo, currently)
        "cornerLargeStickerInsetRatio": 0.043,
        // cornerSmallStickerInsetRatio: ratio of width and height to inset (move toward center) stickers when placed in corner as "small"
        "cornerSmallStickerInsetRatio": 0.05,
        // cornerUserImagePadding: padding for when the user image is placed in the corner.
        // if the image is placed in left corner, the right padding is used to limit its width and vice versa.
        "cornerUserImagePadding": [null /*ignored*/, 0.4, null, 0.4],
        // cornerUserImageInsetRatio: ratio of width (not height; so its even on all sides) to inset (move toward center) user image when placed in corner
        // See '#42: Corner image placement adjustments (for some devices?)'.
        "cornerUserImageInsetRatio": -0.07, // push out
        // cornerStickerRotationDeg: rotation when placing a sticker in the corner
        "cornerStickerRotationDeg": 20,
        // cornerUserImageRotationDeg: rotation when placing user image in the corner
        "cornerUserImageRotationDeg": 20,
        // cornerLargeStickerWidthRatio: ratio of canvas width for sticker when drawn large
        "cornerLargeStickerWidthRatio": 0.4,
        // borderSpacingRatio: the normalized distance between edges of stickers on the border.
        // This is now only for grips.
        "borderSpacingRatio": null,
    },
    "grip": {
        "bleed": 50,
        "safeAreaInsets": [0.15, 0.15, 0.15, 0.15],
        "userImageMargins": [0.035, 0.035, 0.035, 0.035],
        "snapshotExtraOffsetY": 0,
        "snapshotBorderWidth": 20,
        "snapshotInsetRatio": 0.08,
        "snapshotRotationRandomnessDeg": 5,
        "patternCnt": [3, 3],
        "patternRotationRandomnessDeg": 20,
        "patternTranslationRandomness": 0,
        "patternSpacing": [0.24, 0.25],
        "patternInsets": [0.05, 0.05],
        "patternStickerScale": 0.13,
        "borderRotationRandomnessDeg": 180,
        "borderStickerScale": 0.13,
        "cornerLargeStickerInsetRatio": -0.031,
        "cornerSmallStickerInsetRatio": 0,
        "cornerUserImagePadding": [null /*ignored*/, 0.4, null, 0.4],
        "cornerUserImageInsetRatio": 0.1, // neg values pushes image *away* from corner
        "cornerStickerRotationDeg": 45,
        "cornerUserImageRotationDeg": 20,
        "cornerLargeStickerWidthRatio": 0.4,
        "borderSpacingRatio": 0.166,
    },
    "wallet": {
        "bleed": 50, // tbd
        "safeAreaInsets": [0.0, 0.0, 0.0, 0.0], // draw right up to the edge and let the mask do its work
        "userImageMargins": [0.08, 0.035, 0.28, 0.035],
        "snapshotExtraOffsetY": 0,
        "snapshotBorderWidth": 20,
        "snapshotInsetRatio": 0.08,
        "snapshotRotationRandomnessDeg": 5,
        "patternCnt": [5, 5],
        "patternRotationRandomnessDeg": 20,
        "patternTranslationRandomness": 0.008,
        "patternSpacing": [0.31, 0.22],
        "patternInsets": [-0.02, -0.02],
        "patternStickerScale": 0.1,
        "borderRotationRandomnessDeg": 180,
        "borderStickerScale": 0.1,
        "cornerLargeStickerInsetRatio": 0.031,
        "cornerSmallStickerInsetRatio": 0.06,
        "cornerUserImagePadding": [null /*ignored*/, 0.4, null, 0.4],
        "cornerUserImageInsetRatio": -0.07,
        "cornerStickerRotationDeg": 20, // 30
        "cornerUserImageRotationDeg": 20,
        "cornerLargeStickerWidthRatio": 0.25,
        "borderSpacingRatio": null,
    }
};

// Returns the downscaling to use for placing the bg image based on the smallest bleed inset ratio (or safe area inset if bleed unavailable).
function bgImgDownscaling(device) {
    const edgeInsetRatios = (device.bleedInsets && device.bleedInsets.length === 4)
        ? device.bleedInsets
        : device.safeAreaInsets;
    const smallest = Math.min(...edgeInsetRatios);
    return 1.0 - (2 * smallest);
}

// Returns a new drawing area cropped with the safeAreaInsets
function croppedDrawRect(device, canvasWidth, canvasHeight) {
    const safeAreaInsets = device.safeAreaInsets;
    
    let topMarginRatio = ArrayUtil.top(safeAreaInsets);
    let rightMarginRatio = ArrayUtil.right(safeAreaInsets);
    let bottomMarginRatio = ArrayUtil.bottom(safeAreaInsets);
    let leftMarginRatio = ArrayUtil.left(safeAreaInsets);
    
    const topMargin = topMarginRatio * canvasHeight;
    const rightMargin = rightMarginRatio * canvasWidth;
    const bottomMargin = bottomMarginRatio * canvasHeight;
    const leftMargin = leftMarginRatio * canvasWidth;
    
    const croppedCanvasWidth = canvasWidth - rightMargin - leftMargin;
    const croppedCanvasHeight = canvasHeight - topMargin - bottomMargin;

    return new Rect(leftMargin, topMargin, croppedCanvasWidth, croppedCanvasHeight);
}

function renderSettings(product, settingName) {
    return settings[product][settingName];
}

export { renderSettings, bgImgDownscaling, croppedDrawRect };